/**
 * Playgama Bridge SDK — lifecycle, storage, ads, audio/pause sync.
 * Non-Playgama builds set VITE_WITH_PLAYGAMA=false so this module never touches window.bridge.
 */

import { devLog } from "./dev-log.js";

var initPromise = null;
var enabled = false;
var gameplayActive = false;
var audioSyncInstalled = false;
var pauseSyncInstalled = false;
/** @type {{ setMuted?: (muted: boolean) => void, setBgmPaused?: (paused: boolean) => void } | null} */
var audioSyncSoundManager = null;

var BRIDGE_SCRIPT_URL = "https://bridge.playgama.com/v1/stable/playgama-bridge.js";
var CONFIG_PATH = "./playgama-bridge-config.json";

function isPlaygamaBuild() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_PLAYGAMA === "true";
}

function getBridge() {
  if (!isPlaygamaBuild()) return null;
  return typeof window !== "undefined" && window.bridge ? window.bridge : null;
}

function refreshEnabled() {
  var b = getBridge();
  if (!b) {
    enabled = false;
    return false;
  }
  if (b.platform && typeof b.platform.isSupported === "boolean") {
    enabled = b.platform.isSupported;
    return enabled;
  }
  enabled = true;
  return enabled;
}

function safePlatformMessage(message, payload) {
  if (!enabled) return;
  var b = getBridge();
  if (!b || !b.platform || typeof b.platform.sendMessage !== "function") return;
  try {
    if (payload != null) {
      b.platform.sendMessage(message, payload);
    } else {
      b.platform.sendMessage(message);
    }
  } catch (e) {
    console.warn("[PlaygamaBridge] sendMessage(" + message + ") failed:", e);
  }
}

function getEventName(bridge, name) {
  if (!bridge || !bridge.EVENT_NAME) return name;
  return bridge.EVENT_NAME[name] != null ? bridge.EVENT_NAME[name] : name;
}

function isHostAudioEnabled(bridge) {
  if (bridge && bridge.platform && typeof bridge.platform.isAudioEnabled === "boolean") {
    return bridge.platform.isAudioEnabled;
  }
  return true;
}

function muteGameAudio(soundManager) {
  if (!soundManager || typeof soundManager.setMuted !== "function") return;
  soundManager.setMuted(true);
  if (typeof soundManager.setBgmPaused === "function") {
    soundManager.setBgmPaused(true);
  }
}

function restoreGameAudio(soundManager) {
  if (!soundManager || typeof soundManager.setMuted !== "function") return;
  var enabled = isHostAudioEnabled(getBridge());
  soundManager.setMuted(!enabled);
  if (enabled && typeof soundManager.setBgmPaused === "function") {
    soundManager.setBgmPaused(false);
  }
}

function applyInterstitialAudioState(soundManager, state) {
  if (state === "loading" || state === "opened") {
    muteGameAudio(soundManager);
    return;
  }
  if (state === "closed" || state === "failed") {
    restoreGameAudio(soundManager);
  }
}

export const PlaygamaBridge = {
  ensureInitialized: function () {
    if (initPromise) return initPromise;
    var b = getBridge();
    if (!b) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    if (typeof b.initialize !== "function") {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    devLog("[PlaygamaBridge] bridge.initialize()");
    initPromise = b
      .initialize({ configFilePath: CONFIG_PATH })
      .then(function () {
        refreshEnabled();
      })
      .catch(function (err) {
        enabled = false;
        console.warn("[PlaygamaBridge] initialize failed:", err);
      });
    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  getLanguage: function () {
    if (!enabled) return null;
    var b = getBridge();
    if (!b || !b.platform) return null;
    try {
      return b.platform.language || null;
    } catch (e) {
      return null;
    }
  },

  sendGameReady: function () {
    safePlatformMessage("game_ready");
  },

  gameplayStart: function () {
    if (!enabled || gameplayActive) return;
    safePlatformMessage("gameplay_started");
    gameplayActive = true;
  },

  gameplayStop: function () {
    if (!enabled || !gameplayActive) return;
    safePlatformMessage("gameplay_stopped");
    gameplayActive = false;
  },

  showInterstitial: function (placement) {
    if (!enabled) return Promise.resolve();
    if (audioSyncSoundManager) {
      muteGameAudio(audioSyncSoundManager);
    }
    var b = getBridge();
    if (!b || !b.advertisement || typeof b.advertisement.showInterstitial !== "function") {
      return Promise.resolve();
    }
    try {
      var shown = b.advertisement.showInterstitial(placement || undefined);
      if (shown && typeof shown.then === "function") {
        return shown.catch(function (err) {
          console.warn("[PlaygamaBridge] showInterstitial failed:", err);
          if (audioSyncSoundManager) restoreGameAudio(audioSyncSoundManager);
        });
      }
      return Promise.resolve();
    } catch (e) {
      console.warn("[PlaygamaBridge] showInterstitial failed:", e);
      if (audioSyncSoundManager) restoreGameAudio(audioSyncSoundManager);
      return Promise.resolve();
    }
  },

  storageGet: function (keys) {
    var b = getBridge();
    if (!b || !b.storage || typeof b.storage.get !== "function") {
      return Promise.resolve(Array.isArray(keys) ? keys.map(function () { return null; }) : null);
    }
    return b.storage.get(keys).catch(function (err) {
      console.warn("[PlaygamaBridge] storage.get failed:", err);
      return Array.isArray(keys) ? keys.map(function () { return null; }) : null;
    });
  },

  storageSet: function (key, value) {
    if (!enabled) return Promise.resolve();
    var b = getBridge();
    if (!b || !b.storage || typeof b.storage.set !== "function") {
      return Promise.resolve();
    }
    return b.storage.set(key, value).catch(function (err) {
      console.warn("[PlaygamaBridge] storage.set failed:", err);
    });
  },

  /**
   * @param {{ setMuted?: (muted: boolean) => void, setBgmPaused?: (paused: boolean) => void }} soundManager
   */
  installAudioSync: function (soundManager) {
    if (audioSyncInstalled) return;
    if (!soundManager || typeof soundManager.setMuted !== "function") return;

    audioSyncSoundManager = soundManager;

    PlaygamaBridge.ensureInitialized().then(function () {
      if (audioSyncInstalled) return;
      var b = getBridge();
      if (!b || !b.platform || typeof b.platform.on !== "function") return;

      audioSyncInstalled = true;

      function applyHostAudioState(audioEnabled) {
        if (!soundManager || typeof soundManager.setMuted !== "function") return;
        soundManager.setMuted(audioEnabled === false);
      }

      try {
        applyHostAudioState(isHostAudioEnabled(b));
      } catch (e0) {
        console.warn("[PlaygamaBridge] reading platform.isAudioEnabled failed:", e0);
      }

      try {
        if (b.advertisement && typeof b.advertisement.interstitialState === "string") {
          applyInterstitialAudioState(soundManager, b.advertisement.interstitialState);
        }
      } catch (e1) {
        console.warn("[PlaygamaBridge] reading interstitialState failed:", e1);
      }

      try {
        b.platform.on(getEventName(b, "AUDIO_STATE_CHANGED"), function (audioEnabled) {
          applyHostAudioState(audioEnabled);
        });
      } catch (e) {
        audioSyncInstalled = false;
        console.warn("[PlaygamaBridge] AUDIO_STATE_CHANGED listener failed:", e);
      }

      if (b.advertisement && typeof b.advertisement.on === "function") {
        try {
          b.advertisement.on(getEventName(b, "INTERSTITIAL_STATE_CHANGED"), function (state) {
            applyInterstitialAudioState(soundManager, state);
          });
        } catch (e2) {
          console.warn("[PlaygamaBridge] INTERSTITIAL_STATE_CHANGED listener failed:", e2);
        }
      }
    });
  },

  /**
   * @param {{ setPlayerPaused?: (paused: boolean) => void, setFallPaused?: (paused: boolean) => void, stateMachine?: { is: (s: string) => boolean }, runPhase?: { state?: string } }} game
   */
  installPauseSync: function (game) {
    if (pauseSyncInstalled) return;
    var setPaused = game && (typeof game.setPlayerPaused === "function"
      ? game.setPlayerPaused.bind(game)
      : (typeof game.setFallPaused === "function" ? game.setFallPaused.bind(game) : null));
    if (!setPaused) return;

    PlaygamaBridge.ensureInitialized().then(function () {
      if (pauseSyncInstalled) return;
      var b = getBridge();
      if (!b || !b.platform || typeof b.platform.on !== "function") return;

      pauseSyncInstalled = true;

      try {
        b.platform.on(getEventName(b, "PAUSE_STATE_CHANGED"), function (paused) {
          if (!game.stateMachine || !game.stateMachine.is("fall")) return;
          if (!game.runPhase || game.runPhase.state !== "playing") return;
          setPaused(!!paused);
        });
      } catch (e) {
        pauseSyncInstalled = false;
        console.warn("[PlaygamaBridge] PAUSE_STATE_CHANGED listener failed:", e);
      }
    });
  },
};

export { BRIDGE_SCRIPT_URL, CONFIG_PATH };